import React, { useState, useEffect, useRef } from 'react'
import { FormattedMessage, useIntl } from 'react-intl';
import { CreateUrl, CallGetAPI, CallPutAPI, CallPostAPI } from 'Utils/ApiHelper.js';
import { Accordion, Item, AccordionTypes } from 'devextreme-react/accordion';
import NewGoogleMaps2024MultiPolygon from '../../components/Map/NewGoogleMaps2024MultiPolygon'
import { APIGetLoggersModel, BuildApiGetLoggers } from '../../models/APIGetLoggersModel'
import yellowMarker from '../../assets/mapLocationMarkers/pinYellow.png';
import GoogleMapsMobileDash from '../../components/Map/GoogleMapsMobileDash'
import { isLCLLogger } from 'variables/chariotProductIds';
import { Redirect } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import DeviceListMobile from '../Devices/DeviceListMobile'
import { GoogleMap } from "@react-google-maps/api";
import type { google } from 'google.maps'
import { usePortalContext } from '../../layouts/Portal'
import { useMobileLandscape, useTabletContext } from '../../Context/ContextProvider'
import { User } from 'oidc-client';
import NonReportingDeviceListMobile from '../Devices/NonReportingDeviceListMobile';
import Loader from '../../Utils/Loader/NewLoader2025'
import PoorSignalMobile from '../Devices/PoorSignalMobile';
import LowBatteryMobile from '../Devices/LowBatteryMobile'
import FirmwareUpdatesMobile from '../Devices/FirmwareUpdatesMobile'

interface DashStats {
    name: string
    count: number
}

interface StatsData {
    totalLoggers: string | number
    nonReporting: string | number
    firmware: string | number
    alarm: string | number
    battery: string | number
    signal: string | number
}

interface GPS {
    position: { lat: number, lng: number }
    icon: string
    serial: number
    id: number
    deviceName: string
}

interface RedirectProps {
    serial: number
}

interface Coordinates {
    lat: number
    lng: number
}

interface PortalContext {
    user: User
    deviceData: Array<APIGetLoggersModel>
    userLevel: string
}



const MobileDashboard: React.FC = () => {
    //context / hooks
    const isLandscape = useMobileLandscape()
    const isTablet = useTabletContext()
    const intl = useIntl()
    const portalContext: PortalContext = usePortalContext()

    //refs
    const mapRef = useRef<GoogleMap | null>(null)
    
    
    const defaultCenter = window.location.hostname.includes("zonelog.net")
        ? { lat: 47.17442830752493, lng: 8.517189402835617 }
        : { lat: 50.744094943532446, lng: -3.99597602646894 };

    const overViewStyle = {
        backgroundColor: "#f7f7f7",
        borderRadius: "5px",
        padding: "12px 10px 0 10px",
        width: "100%",
        boxShadow: "2px 2px 10px #00000060",
        margin: "0 5px 20px 5px",
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: 'space-between'

    }
    const inactiveTimeFrame = sessionStorage.getItem('inactiveTimeframe') !== 'undefined' ? sessionStorage.getItem('inactiveTimeframe') : '24 Hours'
    const [statsData, setStatsData] = useState<StatsData | null>(null)
    const [markers, setMarkers] = useState<Array<GPS>>([])
    const [clickedMarker, setClickedMarker] = useState<number | null | undefined>()
    const [showTooltip, setShowTooltip] = useState<boolean>(false)
    const [redirect, setRedirect] = useState<boolean>(false)
    const [redirectPath, setRedirectPath] = useState<string>("")
    const [redirectProps, setRedirectProps] = useState<RedirectProps | null>()
    const [loading, setLoading] = useState<boolean>(false)
    const [center, setCenter] = useState<Coordinates>(defaultCenter)
    const [googleMapInstance, setGoogleMapInstance] = useState<google.maps.Map | null>()
    const [selectedIndex, setSelectedIndex] = useState<number>(-1)

    useEffect(() => {
        getDeviceData()
    }, [])

    useEffect(() => {
        fitBoundsToMarkers()
    }, [markers, googleMapInstance])

    const onMapLoad = (map: google.maps.Map): void => {
        map.setMapTypeId("hybrid");
        map.setZoom(15);
        setGoogleMapInstance(map)
    };

    const fitBoundsToMarkers = ():void => {
        if (googleMapInstance && markers.length > 0) {
            const bounds = new window.google.maps.LatLngBounds();
            markers.forEach((marker) => {
                bounds.extend(marker.position);
            });
            googleMapInstance.fitBounds(bounds);
        }
    };

    useEffect(() => {
            getDeviceData()
    }, [portalContext.deviceData])

    const getDeviceData = async (): Promise<void> => {
        if (!portalContext.deviceData) return
        setLoading(true)
        try {
            const data = portalContext.deviceData
            console.log(data)
            const deviceDataTemp: APIGetLoggersModel[] = []
            const markersTemp: GPS[] = []
            data.forEach((device: APIGetLoggersModel) => {
                if (device.GpsLatitude && device.GpsLongitude) {
                    let deviceName = device.Site
                    if (deviceName.length > 12) {
                        deviceName = `${deviceName.slice(0,12)}...`
                    }
                    const mark: GPS = {
                        position: {
                            lat: device.GpsLatitude,
                            lng: device.GpsLongitude
                        },
                        icon: yellowMarker,
                        serial: device.Serial,
                        id: device.Id,
                        deviceName: deviceName
                    }
                    markersTemp.push(mark)
                }
            })
            GetDashStats()
            setMarkers(markersTemp)
        } catch (err) {
            console.error(err)
        }
        setLoading(false)
           
    }

    const GetDashStats = async (): Promise<void> => {
        let dashboardData = []
        try {
            if (sessionStorage.getItem('filterGroupId') === '0') {
                dashboardData = await CallGetAPI(CreateUrl('/api/Dashboard/dashboardStoredStats?companyid=' + sessionStorage.getItem('companyId')), {})
            } else {
                dashboardData = await CallGetAPI(CreateUrl('/api/Dashboard/dashboardstats?companyid=' + sessionStorage.getItem('companyId') + '&filterGroup=' + sessionStorage.getItem('filterGroupId')), {})
            }
        } catch (err) {
            console.error(err)
        }

        const p: StatsData = {
            totalLoggers: 0,
            nonReporting: 0,
            firmware: 0,
            alarm: 0,
            battery: 0,
            signal: 0
        }
        for (let i = 0; i < dashboardData.length; i++) {
            if (i === 0 || i === 1) {
                p.totalLoggers += dashboardData[i].count
            }
            if (inactiveTimeFrame === "6 Hours" && i === 2) {
                p.nonReporting = dashboardData[i].count
            }
            if (inactiveTimeFrame === "12 Hours" && i === 3) {
                p.nonReporting = dashboardData[i].count
            }
            if (inactiveTimeFrame === "24 Hours" && i === 4) {
                p.nonReporting = dashboardData[i].count
            }
            if (inactiveTimeFrame === "3 Days" && i === 5) {
                p.nonReporting = dashboardData[i].count
            }
            if (inactiveTimeFrame === "4 Days" && i === 6) {
                p.nonReporting = dashboardData[i].count
            }
            if (inactiveTimeFrame === "7 Days" && i === 7) {
                p.nonReporting = dashboardData[i].count
            }
            if (inactiveTimeFrame === "14 Days" && i === 8) {
                p.nonReporting = dashboardData[i].count
            }
            if (i === 9) {
                p.firmware = dashboardData[i].count
            }
            if (i === 10) {
                p.alarm = dashboardData[i].count
            }
            if (i === 11) {
                p.battery = dashboardData[i].count
            }
            if (i === 12) {
                p.signal = dashboardData[i].count
            }
        }
        const stringifiedStats: StatsData | null = {};
        for (const [key, value] of Object.entries(p)) {
            stringifiedStats[key] = String(value);
        }
        console.log(stringifiedStats);
        setStatsData(stringifiedStats);
    }

    const handleMarkerClick = (marker: GPS): void => {
        setClickedMarker(marker.id)
        setShowTooltip(true)
    }

    const tooltipClose = ():void => {
        setShowTooltip(false)
    }

    const deviceOverviewClick = (serial: number | null): void => {
        console.log('clicked')
        const model = portalContext.deviceData?.filter(dev => dev.Serial === serial)[0].Model
        console.log(model, serial)
                setRedirect(true)
                setRedirectPath(!isLCLLogger(model) ? '/portal/DeviceDetail' : '/portal/LCLDeviceDetail')
                setRedirectProps({
                    serial: serial
                })
        window.scrollTo(0, 0);
    }

    const deviceGraphClick = (id: number):void => {
        console.log(id)
        sessionStorage.setItem('selectedLoggers', String(id))
        setRedirect(true)
        setRedirectPath('/portal/multiLoggerGraph')
        setRedirectProps(
            {
                groupId: null,
                loggers: [id],
                loggerId: null,
                showBackButton: true,
                serial: null
            }
        )
    }


    useEffect(() => {

        const selectedDropDown = document.querySelector('.dx-accordion-item-opened')
        selectedDropDown?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        
    }, [selectedIndex])
   

    return (
        !redirect ? (<div>
            {loading ?
                    <div style={{
                        position: 'relative', left: 'Calc(50% - 100px)', top: 'Calc(50vh - 200px)'
                    }}>
                        <Loader color={undefined} />
                    </div> :
                <div>
                    <div style={{ display: "flex", width: (isLandscape || isTablet) ? "100%" : "110%", justifyContent: "space-between", transform: (isLandscape || isTablet) ? 'none' : 'translateX(-5%)' }}>
                        <div style={{ ...overViewStyle, borderLeft: "10px solid #00bf00" }} onClick={(): void => setSelectedIndex(0)}>
                        <p><FormattedMessage id='All Devices' /></p>
                        <h5 style={{ textAlign: "right" }}><b>{statsData?.totalLoggers ? statsData?.totalLoggers : '...loading'}</b></h5>
                    </div>
                        <div style={{ ...overViewStyle, borderLeft: "10px solid #009fe3" }} onClick={():void => setSelectedIndex(1)}>
                        <p><FormattedMessage id='Non Reporting' /></p>
                        <h5><b>{statsData?.nonReporting ? statsData?.nonReporting : '...loading'}</b></h5>
                    </div>
                </div>
                    <div style={{ display: "flex", width: (isLandscape || isTablet) ? "100%" : "110%", justifyContent: "space-between", transform: (isLandscape || isTablet) ? 'none' : 'translateX(-5%)' }}>
                        <div style={{ ...overViewStyle, borderLeft: "10px solid #00ADBA" }} onClick={(): void => setSelectedIndex(2)}>
                        <p><FormattedMessage id='Poor Signal' /></p>
                        <h5 style={{ textAlign: "right" }}><b>{statsData?.signal ? statsData?.signal : '...loading'}</b></h5>
                    </div>
                        <div style={{ ...overViewStyle, borderLeft: "10px solid #6f2da8" }} onClick={(): void => setSelectedIndex(3)}>
                        <p><FormattedMessage id='Low Battery' /></p>
                        <h5><b>{statsData?.battery ? statsData?.battery : '...loading'}</b></h5>
                    </div>
                </div>
                    <div style={{ display: "flex", width: (isLandscape || isTablet) ? "100%" : "110%", justifyContent: "space-between", transform: (isLandscape || isTablet) ? 'none' : 'translateX(-5%)' }}>
                        <div style={{ ...overViewStyle, borderLeft: "10px solid #ff6700" }} onClick={(): void => setSelectedIndex(4)}>
                        <p><FormattedMessage id='Updates Due' /></p>
                        <h5><b>{statsData?.firmware ? statsData?.firmware : '...loading'}</b></h5>
                    </div>
                        <div style={{ ...overViewStyle, borderLeft: "10px solid red" }} onClick={(): void => setSelectedIndex(5)}>
                        <p><FormattedMessage id='In Alarm' /></p>
                        <h5><b>{statsData?.alarm ? statsData?.alarm : '...loading'}</b></h5>
                    </div>
                </div>
                {/* Map */}
                    {portalContext.deviceData.length > 0 && <GoogleMapsMobileDash
                        onMapLoad={onMapLoad}
                        markers={markers}
                        handleMarkerClick={handleMarkerClick}
                        showTooltip={showTooltip}
                        clickedMarker={clickedMarker}
                        handleTooltipCloseClick={tooltipClose}
                        deviceOverviewClick={deviceOverviewClick}
                        center={center}
                        deviceGraphClick={deviceGraphClick}
                    />}
                    <div style={{ width: (isLandscape || isTablet) ? "100%" : "120%", transform: (isLandscape || isTablet) ? "none" : "translateX(-8%)"} }>
                    <Accordion
                            collapsible={true}
                            multiple={true}
                            style={{ background: '#eeeeee', color: "black", marginTop: "20px" }}
                            selectedIndex={selectedIndex}
                            onSelectedIndexChange={(idx):void => setSelectedIndex(idx) }

                        
                    >
                            {portalContext.deviceData.length > 0  && <Item title={intl.formatMessage({ id: "Devices" })}>
                            <div>
                                <DeviceListMobile deviceDataProps={portalContext.deviceData} />
                            </div>
                        </Item>}
                        <Item title={intl.formatMessage({ id: 'Non Reporting (user defined)' }) }>
                            <div>
                                <NonReportingDeviceListMobile />
                            </div>
                        </Item>
                        <Item title={intl.formatMessage({ id: 'Poor Signal' })}>
                            <div>
                                <PoorSignalMobile />
                            </div>
                        </Item>
                        <Item title={intl.formatMessage({ id: 'Low Battery' })}>
                            <div>
                                <LowBatteryMobile />
                            </div>
                            </Item>
                        <Item title={intl.formatMessage({ id: 'Firmware Updates' })}>
                            <div>
                                <FirmwareUpdatesMobile />
                            </div>
                        </Item>
                    </Accordion>
                </div>
            </div>}
        </div>) :
            (<Redirect push to={{
                pathname: redirectPath,
                state: redirectProps
            }} />)
    )
}
export default MobileDashboard